<template>
  <section-page title="Exports">
    <app-loading-section
      :loading="initialLoading"
      class="exports-page columns"
    >
      <div class="column is-5">
        <form-field-select
          label="Lead Location"
          v-model="recordFilter.recordLocations"
          @change.native="getPreviewCount"
          size=""
        >
          <option
            v-for="(location, index) in recordLocations"
            :value="location.value"
            :key="index"
          >{{ location.label }}</option>
        </form-field-select>

        <form-field-select
          v-show="showStatusDropdown"
          :label="stateStatusFieldLabel + ' Statuses'"
          v-model="recordFilter.statuses"
          @change.native="getPreviewCount"
          size=""
        >
          <option
            v-for="stateStatus in stateStatuses"
            :value="stateStatus.id"
            :key="stateStatus.id"
          >{{ stateStatus.label }}</option>
        </form-field-select>

        <form-field-select
          v-show="showFUSDropdown"
          label="Follow Up Sequence"
          v-model="recordFilter.fusTemplates"
          @change.native="getPreviewCount"
          size=""
        >
          <option
            v-for="fus in fusTemplates"
            :value="fus.id"
            :key="fus.id"
          >{{ fus.label }}</option>
        </form-field-select>

        <form-field-select
          label="Users"
          v-model="recordFilter.users"
          @change.native="getPreviewCount"
          size=""
          :disabled="!this.$store.getters.authUser.can_access_all_leads"
        >
          <option
            v-for="user in users"
            :value="user.id"
          >{{ user.full_name }}</option>
        </form-field-select>

        <form-field-select
          label="Campaigns"
          v-model="recordFilter.campaigns"
          @change.native="getPreviewCount"
          size=""
        >
          <option
            v-for="camp in campaigns"
            :value="camp.id"
          >{{ camp.label }}</option>
        </form-field-select>

        <form-field-select
          label="Exit Strategies"
          v-model="recordFilter.exitStrategies"
          @change.native="getPreviewCount"
          size=""
        >
          <option
            v-for="exit in exitStrategies"
            :value="exit.value"
          >{{ exit.label }}</option>
        </form-field-select>

        <form-field-select
          label="Last Modified"
          v-model="recordFilter.timeFrames"
          @change.native="getPreviewCount"
          size=""
        >
          <option
            v-for="time in timeFrames"
            :value="time.value"
          >{{ time.label }}</option>
        </form-field-select>

        <form-field-select
          label="Created At"
          v-model="recordFilter.createdAt"
          @change.native="getPreviewCount"
          size=""
        >
          <option
            v-for="time in timeFrames"
            :value="time.value"
          >{{ time.label }}</option>
          <option :value="createdAtCustomValue">Custom</option>
        </form-field-select>

        <form-field-date-picker
          v-if="showDateRangeDropdown"
          label="Date Range"
          v-model="createdAtCustomDates"
          is-range
          size=""
          @change="getPreviewCount"
        ></form-field-date-picker>

        <p class="preview_count">Total Leads Found: <strong>{{ previewCount }}</strong></p>

        <p
          v-if="disableButtons"
          class="download_limit"
        >We are unable to export more than {{ downloadLimit }} leads. Please use the filters above to lower the number of leads you want to export. We recommend using the location filter.</p>

        <grid-row class="my-100">
          <grid-column>
            <button-outlined
              @click="getPreview"
              icon-left="file-find"
              class="is-fullwidth"
              :disabled="disableButtons"
              size=""
            >Preview</button-outlined>
          </grid-column>

          <grid-column>
            <button-default
              @click="downloadLeads"
              icon-left="download"
              class="is-fullwidth"
              :disabled="disableButtons"
              size=""
            >Download</button-default>
          </grid-column>
        </grid-row>
      </div>

      <div class="column is-7">
        <app-loading-section
          :loading="isLoadingPreview"
          :count="recordPreview.length"
          heading="Click the Preview Button to view the leads that will be downloaded"
        >
          <div class="preview-item" v-for="record in recordPreview">
            <p class="preview-item--title" @click="openLeadModal(record.id)">{{ record.property_street_address }} | {{record.primary_contact_name }}</p>
            <p>Status: {{ record.status }}</p>
          </div>
        </app-loading-section>
      </div>
    </app-loading-section>
  </section-page>
</template>

<script>
import { timeFrames } from '../../services/staticData';
import {ENUMS} from "../../services/enums";
import dates from "../../services/dates";

export default {
  title: 'ExportsTab',
  data() {
    return {
      initialLoading: true,
      isLoadingPreview: false,
      query: '?',
      recordPreview: [],
      previewCount: 0,
      disableButtons: true,
      stateStatusFieldLabel: '',
      recordFilter: {
        recordLocations: null,
        fusTemplates: null,
        statuses: null,
        users: this.$store.getters.authUser.can_access_all_leads ? null : this.$store.getters.authUser.id,
        campaigns: null,
        exitStrategies: null,
        timeFrames: null,
        timeFramesValues: [],
        createdAt: null,
        createdAtValues: [],
        returnCount: false,
        isPreview: false,
      },
      createdAtCustomDates: [],
      createdAtCustomValue: 'custom',
      downloadLimit: parseInt(process.env.MIX_MAX_DOWNLOAD_COUNT)
    }
  },
  watch: {
    'recordFilter.recordLocations'() {
      // reset these values everytime recordLocations changes
      this.recordFilter.statuses = null;
      this.recordFilter.fusTemplates = null;
    },
  },
  mounted() {
    this.getPreviewCount();
  },
  computed: {
    recordLocations() {
      return [
        { value: null, label: 'All Leads' },
        ...this.$store.state.enums.statusStateEnum,
      ]
    },
    contactingStatuses() {
      return [
        { id: null, label: 'All Leads in the Contact Not Made' },
        ...this.$store.getters['pipeline/contactingStatuses']
      ]
    },
    activeStatuses() {
      return [
        { id: null, label: 'All Leads in the Active' },
        ...this.$store.getters['pipeline/activeStatuses']
      ]
    },
    followUpStatuses() {
      return [
        { id: null, label: 'All Leads in the Follow Up' },
        ...this.$store.getters['pipeline/followUpStatuses']
      ]
    },
    completedStatuses() {
      return [
        { id: null, label: 'All Leads in the Completed' },
        ...this.$store.getters['pipeline/completedStatuses']
      ]
    },
    deadStatuses() {
      return [
        { id: null, label: 'All Leads in the Dead' },
        ...this.$store.getters['pipeline/deadStatuses']
      ]
    },
    nonLeadStatuses() {
      return [
        { id: null, label: 'All Leads in the Non Lead' },
        ...this.$store.getters['pipeline/nonLeadStatuses']
      ]
    },
    stateStatuses() {
      if (this.recordFilter.recordLocations === ENUMS.StatusStateEnum.Contacting.value) {
        this.stateStatusFieldLabel = ENUMS.StatusStateEnum.Contacting.label;
        return this.contactingStatuses
      }

      if (this.recordFilter.recordLocations === ENUMS.StatusStateEnum.Active.value) {
        this.stateStatusFieldLabel = ENUMS.StatusStateEnum.Active.label;
        return this.activeStatuses
      }

      if (this.recordFilter.recordLocations === ENUMS.StatusStateEnum.FollowUp.value) {
        this.stateStatusFieldLabel = ENUMS.StatusStateEnum.FollowUp.label;
        return this.followUpStatuses
      }

      if (this.recordFilter.recordLocations === ENUMS.StatusStateEnum.Completed.value) {
        this.stateStatusFieldLabel = ENUMS.StatusStateEnum.Completed.label;
        return this.completedStatuses
      }

      if (this.recordFilter.recordLocations === ENUMS.StatusStateEnum.Dead.value) {
        this.stateStatusFieldLabel = ENUMS.StatusStateEnum.Dead.label;
        return this.deadStatuses
      }

      if (this.recordFilter.recordLocations === ENUMS.StatusStateEnum.NonLead.value) {
        this.stateStatusFieldLabel = ENUMS.StatusStateEnum.NonLead.label;
        return this.nonLeadStatuses
      }

      this.stateStatusFieldLabel = '';
      return [];
    },
    fusTemplates() {
      return [
        { id: null, label: 'All Leads in Follow Up Sequences' },
        ...this.$store.getters.fusTemplates
      ]
    },
    users() {
      return [
        { id: null, full_name: 'No Filter' },
        { id: 'noUsers', full_name: 'No Users' },
        { id: 'allUsers', full_name: 'All Users' },
        ...this.$store.getters.activeUsers,
      ];
    },
    campaigns() {
      return [
        { id: null, label: 'No Filter' },
        { id: 'noCampaigns', label: 'No Campaigns' },
        { id: 'allCampaigns', label: 'All Campaigns' },
        ...this.$store.getters.campaigns,
      ];
    },
    exitStrategies() {
      return [
        { value: null, label: 'No Filter' },
        ...this.$store.state.enums.exitStrategyEnum
      ];
    },
    timeFrames() {
      return [
        { value: null, label: 'No Filter' },
        ...timeFrames
      ];
    },
    hasFollowUpSequenceFeature() {
      return this.$store.getters.account.has_follow_up_sequence_feature;
    },
    showStatusDropdown() {
      return this.recordFilter.recordLocations !== null && this.stateStatuses.length !== 0
    },
    showFUSDropdown() {
      if(! this.hasFollowUpSequenceFeature) return false;

      return this.recordFilter.recordLocations !== null && this.recordFilter.recordLocations ===
        ENUMS.StatusStateEnum.FollowUp.value
    },
    showDateRangeDropdown() {
      return this.recordFilter.createdAt === this.createdAtCustomValue
    }
  },
  methods: {
    setExportFilterValues() {
      const timeFrameValue = timeFrames.find(t => t.value === this.recordFilter.timeFrames);
      const createdAtValue = timeFrames.find(t => t.value === this.recordFilter.createdAt);

      this.recordFilter.timeFramesValues = [];
      this.recordFilter.createdAtValues = [];

      if (timeFrameValue) {
        this.recordFilter.timeFramesValues = {
          start: timeFrameValue.start,
          end: timeFrameValue.end,
        }
      }

      if (createdAtValue && this.recordFilter.createdAt !== this.createdAtCustomValue) {
        this.createdAtCustomDates = [];
        this.recordFilter.createdAtValues = {
          start: createdAtValue.start,
          end: createdAtValue.end,
        }
      }

      if (this.createdAtCustomDates.length && this.recordFilter.createdAt === this.createdAtCustomValue) {
        this.recordFilter.createdAtValues = {
          start: dates.convertJsDateToStartOf(this.createdAtCustomDates[0]),
          end: dates.convertJsDateToEndOf(this.createdAtCustomDates[1]),
        }
      }
    },
    getPreviewCount() {
      this.recordFilter.returnCount = true
      this.recordFilter.isPreview = false
      this.setExportFilterValues()

      this.$api.exports.export(this.recordFilter).then(res => {
        this.recordPreview = [];
        this.previewCount = res.data;
        this.disableButtons = this.previewCount >= this.downloadLimit;
        this.initialLoading = false;
      });
    },
    getPreview() {
      this.isLoadingPreview = true;
      this.recordFilter.returnCount = false
      this.recordFilter.isPreview = true
      this.setExportFilterValues()

      this.$api.exports.export(this.recordFilter).then(res => {
        this.recordPreview = res.data
        this.isLoadingPreview = false;
      });
    },
    downloadLeads() {
      this.recordFilter.returnCount = false
      this.recordFilter.isPreview = false
      this.setExportFilterValues()

      this.$api.exports.export(this.recordFilter).then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');

        link.href = url;
        link.setAttribute('download', 'records.csv');
        document.body.appendChild(link);
        link.click();
      });
    },
    convertCsv(csv) {
      return new Promise((resolve, reject) => {
        const lines= csv.split("\n");
        const result = [];
        const headers = lines[0].split(",");

        for (let i = 1; i < lines.length; i++) {
          const obj = {};

          let row = lines[i],
            queryIdx = 0,
            startValueIdx = 0,
            idx = 0;

          if (row.trim() === '') { continue; }

          while (idx < row.length) {
            /* if we meet a double quote we skip until the next one */
            let c = row[idx];

            if (c === '"') {
              do { c = row[++idx]; } while (c !== '"' && idx < row.length - 1);
            }

            if (c === ',' || /* handle end of line with no comma */ idx === row.length - 1) {
              /* we've got a value */
              let value = row.substr(startValueIdx, idx - startValueIdx).trim();

              /* skip first double quote */
              if (value[0] === '"') { value = value.substr(1); }
              /* skip last comma */
              if (value[value.length - 1] === ',') { value = value.substr(0, value.length - 1); }
              /* skip last double quote */
              if (value[value.length - 1] === '"') { value = value.substr(0, value.length - 1); }

              const key = headers[queryIdx++].replace(/^"(.*)"$/, '$1');
              obj[key] = value;
              startValueIdx = idx + 1;
            }

            ++idx;
          }

          result.push(obj);
        }

        resolve(result);
      });
    },
    openLeadModal(id) {
      this.$router.replace({query: {lead: id}})
    },
  }
}
</script>

<style lang="scss" scoped>
@import "../../styles/variables/variables";

.preview_count {
  margin-bottom: 20px;
  font-size: 1.25rem;
}

.download_limit {
  line-height: 1.3;
  color: $red;
}

.preview-item {
  font-size: 11px;
  font-weight: bold;
  padding: 10px;
  border-bottom: 1px solid #eee;

  &--title {
    margin-bottom: 5px;
    color: $secondary;
    cursor: pointer;

    &:hover {
      color: $primary;
    }
  }
}
</style>
